import { createContext, useContext, useMemo } from "react";

import { useRouteMatch } from "react-router-dom";

import { type IWebviewRouteParams, type MobileWebview } from "./types";

export type WebviewRouteType = "root" | "child";

/**
 * Webview context containing route parameters, root path, and native event handlers
 */
export interface IWebviewContextState extends IWebviewRouteParams {
  /** The route path pattern for the root webview route */
  rootPath: string;
  /** Native event handlers injected by the mobile app, undefined if not in a native webview */
  native: MobileWebview;
}

/**
 * Extended webview context returned by useWebviewContext, includes computed routeType
 */
export interface IWebviewContextResult extends IWebviewContextState {
  /**
   * The type of webview route:
   * - "root": component rendered directly by WebviewPageLayout
   * - "child": nested route within the webview
   */
  routeType: WebviewRouteType;
}

/**
 * Context containing webview-specific data, such as platform and mobileVersion.
 *
 * This context is only available within webview routes and will be undefined
 * in non-webview contexts.
 */
const WebviewContext = createContext<IWebviewContextState | undefined>(
  undefined,
);

export interface IWebviewContextProviderProps {
  value: IWebviewContextState;
  children: React.ReactNode;
}
export function WebviewContextProvider({
  children,
  value,
}: IWebviewContextProviderProps) {
  return (
    <WebviewContext.Provider value={value}>{children}</WebviewContext.Provider>
  );
}

/**
 * Hook to access webview context data including route type.
 *
 * @returns The webview context with routeType, or undefined if not in a webview route
 *
 * @example
 * ```tsx
 * const webview = useWebviewContext();
 * if (webview?.platform === 'ios') {
 *   // iOS-specific logic
 * }
 *
 * if (webview?.routeType === 'root') {
 *   // On the root webview route
 * }
 *
 * // Access native handlers in a type-safe way
 * webview?.native?.onGoBack();
 * ```
 */
export function useWebviewContext(): IWebviewContextResult | undefined {
  const context = useContext(WebviewContext);
  const match = useRouteMatch();

  return useMemo(() => {
    if (!context) {
      return undefined;
    }

    const routeType: WebviewRouteType =
      match?.path === context.rootPath ? "root" : "child";

    return {
      ...context,
      routeType,
    };
  }, [context, match?.path]);
}
